import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Copy, Check, ExternalLink, Info, X } from "lucide-react";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";

interface ReceiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  walletAddress?: string;
  explorerBaseUrl?: string;
}

const ReceiveModal: React.FC<ReceiveModalProps> = ({
  isOpen,
  onClose,
  walletAddress = "0x1a2b3c4d5e6f7g8h9i0j1k2l3m4n5o6p7q8r9s0t",
  explorerBaseUrl = "https://testnet.cotiscan.io",
}) => {
  const [copied, setCopied] = useState(false);

  useBodyScrollLock(isOpen);

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(walletAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleViewOnExplorer = () => {
    window.open(`${explorerBaseUrl}/address/${walletAddress}`, "_blank");
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4"
        >
          <div className="absolute inset-0 modal-overlay-blur" onClick={onClose} />

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-md max-h-[92dvh] overflow-y-auto overscroll-contain rounded-t-2xl sm:rounded-2xl sm:max-h-none sm:overflow-visible border border-border modal-compact p-4 sm:p-5 shadow-2xl"
          >
            {/* Header */}
            <div className="mb-4 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-foreground sm:text-xl">Receive Tokens</h2>
                <p className="text-xs text-muted-foreground sm:hidden">Share your address to receive tokens.</p>
              </div>
              <button
                onClick={onClose}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:border-border hover:text-foreground sm:h-8 sm:w-8"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Address */}
            <div className="mb-3">
              <label className="mb-1 block text-[10px] uppercase tracking-wider text-muted-foreground">
                Your Address
              </label>
              <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2">
                <span
                  className="min-w-0 flex-1 font-mono text-[10px] sm:text-xs text-muted-foreground truncate"
                  title={walletAddress}
                >
                  {walletAddress}
                </span>
                <button
                  type="button"
                  onClick={handleCopyAddress}
                  className="flex h-8 w-8 shrink-0 items-center justify-center text-muted-foreground hover:text-muted-foreground"
                  aria-label="Copy address"
                >
                  {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* QR Code */}
            <div className="mb-3 flex justify-center">
              <div className="rounded-lg border border-cyan-400/40 bg-white p-2">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(walletAddress)}`}
                  alt="QR Code for wallet address"
                  className="h-36 w-36"
                />
              </div>
            </div>

            {/* Notice */}
            

            {/* Actions */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCopyAddress}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border bg-muted/50 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
              >
                <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                Copy
              </button>
              <button
                type="button"
                onClick={handleViewOnExplorer}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border bg-muted/50 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
              >
                <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                Explorer
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ReceiveModal;
